/**
 * Format a decimal time value into a padded key
 * @param {number} decitime - The decimal time value
 * @returns {string|null} Padded time key or null
 */
export function convertDecitimeToKey(decitime) {
  if (decitime == undefined) return null
  let [seconds, tenths] = decitime.toString().split('.')

  seconds = seconds.padStart(6, '0')
  tenths = tenths ? tenths.padEnd(1, '0') : '0'

  return seconds + tenths
}

/**
 * Extract domain from URL without protocol and www
 * @param {string} url - The URL to process
 * @returns {string} Domain name
 */
export function getDomain(url) {
  return url
    .replace('https://', '')
    .replace('http://', '')
    .replace('www.', '')
    .split('/')[0]
}

/**
 * Extract base domain/hostname from URL
 * @param {string} url - The URL to process
 * @returns {string|null} Hostname or null if invalid
 */
export function getBaseDomain(url) {
  if (!url || !url.includes('http')) {
    return url
  }
  
  try {
    const { hostname } = new URL(url)
    return hostname
  } catch (error) {
    log.info(1,"Invalid URL:", error)
    return null
  }
}

/**
 * Format a number with K suffix for thousands
 * @param {number} number - Number to format
 * @returns {string} Formatted number
 */
export function formatNumber(number) {
  if (number == undefined) {
    return 0
  }
  
  if (number >= 999) {
    return (number / 1000).toFixed(1).replace(/\.0$/, '') + 'K'
  } else if (number >= 1000) {
    return number.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",")
  }
  
  return number.toString()
}

/**
 * Convert seconds to rounded minutes (minimum 1 if seconds > 0)
 * @param {number} seconds - Input seconds
 * @returns {number} Rounded minutes
 */
export function formatMinutes(seconds) {
  if (!seconds || seconds <= 0) return 0
  const mins = Math.round(seconds / 60)
  return mins > 0 ? mins : 1
}


/**
 * Format seconds into HH:MM:SS.D format
 * @param {number} seconds - Seconds to format
 * @param {boolean} decimals - Include decimal places
 * @returns {string} Formatted time string
 */
export function formatSeconds(seconds, decimals = true) {
  if (seconds < 0 || seconds == undefined) {
    return ''
  }

  // Calculate hours, minutes, seconds directly from the duration
  const totalSeconds = Math.floor(seconds)
  const hh = Math.floor(totalSeconds / 3600)
  const mm = Math.floor((totalSeconds % 3600) / 60)
  const ss = totalSeconds % 60

  // Calculate decimal part
  const decimalPart = seconds - totalSeconds
  
  // Format hours
  let hhStr = hh > 0 ? hh + ":" : ""

  // Format minutes
  let mmStr
  if (mm < 10 && mm != 0) {
    mmStr = hhStr === "" ? mm + ":" : "0" + mm + ":"
  } else if (mm >= 10 || mm === 0) {
    mmStr = mm + ":"
  }

  // Format seconds
  let ssStr = ss < 10 ? "0" + ss : ss.toString()

  // Format decimal
  let decimalStr = ''
  if (decimals && decimalPart > 0) {
    const decimalDigit = Math.round(decimalPart * 10)
    if (decimalDigit > 0) {
      decimalStr = '.' + decimalDigit.toString()
    }
  }

  return hhStr + mmStr + ssStr + decimalStr
}

/**
 * Strip HTML tags and JavaScript from a string
 * @param {string} str - String to clean
 * @returns {string} Cleaned string
 */
export function stripHtmlAndJs(str) {
  if (!str) return ''
  
  // Remove HTML tags
  let stripped = str.replace(/<[^>]*>/g, '')
  
  // Remove JavaScript
  stripped = stripped.replace(/javascript:/gi, '')
  stripped = stripped.replace(/on\w+\s*=\s*["']?[^"']*["']?/gi, '')
  
  // Decode HTML entities
  const textarea = document.createElement('textarea')
  textarea.innerHTML = stripped
  return textarea.value
}